import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import CountrySelect from 'components/ui/composed/declarations/CountrySelect';
import DeclarationNumberInput from 'components/ui/composed/declarations/formInput/DeclarationNumberInput';
import FormSelect from 'components/ui/composed/declarations/formSelect/DeclarationSelect';
import useCodelists from 'hooks/useCodelists';
import useTooltips from 'hooks/useTooltips';
import { DeclarationFormCardProps } from 'views/declarations/common/declaration.form.card';
import { getFormikProps, handleToggleHelp } from 'views/declarations/utils/form-utils';
import { validators } from 'views/declarations/uk/export/validations/validations';
import MultipleDeclarationField from '../../../../../common/MultipleDeclarationField';
import NewFormCard, { FormCardContainer } from '../../../../../common/cards/NewFormCard';
import CombinedField from 'components/ui/composed/declarations/combined-field/CombinedField';

const ItemDetailsCard: FC<DeclarationFormCardProps> = (props) => {
    const { t } = useTranslation('form');
    const { aisCodelists } = useCodelists();
    const { getH1TooltipsByRefNumberAndField } = useTooltips();

    return (
        <NewFormCard title={t('Item Details')}>
            <FormCardContainer>
                <CountrySelect
                    required
                    viewOnly={props.viewOnly}
                    {...getFormikProps(`countryOrigin`, props)}
                    refNumber="5.15"
                    label={t('countryOrigin')}
                    tooltip={getH1TooltipsByRefNumberAndField('5.15', t('countryOrigin'))}
                    refClicked={(ref) => handleToggleHelp(ref, props)}
                    condensed
                />

                <DeclarationNumberInput
                    viewOnly={props.viewOnly}
                    {...getFormikProps(`goodsInformation.grossMass`, props)}
                    refNumber="6.5"
                    label={t('goodsItems.grossMass')}
                    refTooltip={getH1TooltipsByRefNumberAndField('6.5', t('goodsItems.grossMass'))}
                    refClicked={(ref) => handleToggleHelp(ref, props)}
                    condensed
                />

                <DeclarationNumberInput
                    required
                    viewOnly={props.viewOnly}
                    {...getFormikProps(`goodsInformation.netMass`, props)}
                    refNumber="6.1"
                    label={t('goodsItems.netMass')}
                    refTooltip={getH1TooltipsByRefNumberAndField('6.1', t('goodsItems.netMass'))}
                    refClicked={(ref) => handleToggleHelp(ref, props)}
                    condensed
                />

                <FormSelect
                    required
                    viewOnly={props.viewOnly}
                    {...getFormikProps(`preference`, props)}
                    refNumber="4.17"
                    label={t('preference')}
                    selectOptions={aisCodelists?.preference}
                    tooltip={getH1TooltipsByRefNumberAndField('4.17', t('preference'))}
                    refClicked={(ref) => handleToggleHelp(ref, props)}
                    condensed
                    codeValidation={[validators.exact(3)]}
                />

                <CountrySelect
                    viewOnly={props.viewOnly}
                    {...getFormikProps(`countryPreferentialOrigin`, props)}
                    refNumber="5.16"
                    label={t('countryPreferentialOrigin')}
                    tooltip={getH1TooltipsByRefNumberAndField('5.16', t('countryPreferentialOrigin'))}
                    refClicked={(ref) => handleToggleHelp(ref, props)}
                    condensed
                />

                <CombinedField
                    refNumber={['1.10', '1.11']}
                    label={t('procedureCodes')}
                    tooltip={getH1TooltipsByRefNumberAndField('1.10', t('requestedProcedure'))}
                    refClicked={(ref) => handleToggleHelp(ref, props)}
                    viewOnly={props.viewOnly}
                    required
                >
                    <FormSelect
                        viewOnly={props.viewOnly}
                        {...getFormikProps(`procedureCode`, props)}
                        placeholder={t('requestedProcedure')}
                        selectOptions={aisCodelists?.procedureCode}
                        condensed
                        codeValidation={[validators.exact(2)]}
                    />
                    <FormSelect
                        viewOnly={props.viewOnly}
                        {...getFormikProps(`previousProcedureCode`, props)}
                        placeholder={t('previousProcedure')}
                        selectOptions={aisCodelists?.procedureCode}
                        condensed
                        codeValidation={[validators.exact(2)]}
                    />
                    <MultipleDeclarationField parent="additionalProcedure" name="additionalProcedure">
                        {(fieldProps, controls, index) => (
                            <div style={index > 0 ? { flexBasis: '100%', minWidth: 0 } : undefined}>
                                <FormSelect
                                    viewOnly={props.viewOnly}
                                    {...getFormikProps(fieldProps.field.name, fieldProps.form)}
                                    placeholder={t('additionalProcedure')}
                                    selectOptions={aisCodelists?.additionalProcedure}
                                    condensed
                                    multipleF={controls}
                                    codeValidation={[validators.exact(3)]}
                                />
                            </div>
                        )}
                    </MultipleDeclarationField>
                </CombinedField>
            </FormCardContainer>
        </NewFormCard>
    );
};

export default ItemDetailsCard;
